import { BitbucketIcon } from '@/components/bitbucket/BitbucketIcon';
import { ColumnFilter } from '@/components/DataTable/components/ColumnFilter';
import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type FilterValue,
} from '@/components/DataTable/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { isAccountMember } from '@/lib/member-kinds';
import { useUser } from '@descope/react-sdk';
import type { OrganizationMember } from '@kirin/api-client/contracts/organization-members';
import {
  DeploymentStatus,
  OrganizationUserStatus,
  PersonalKeyState,
} from '@kirin/api-client/contracts/organization-members';
import { OrganizationRole } from '@kirin/api-client/contracts/organizations';
import { Github, Mail, Shield, ShieldUser, User } from 'lucide-react';
import { useState, type ReactElement } from 'react';
import { RoleBasedRender } from '@/components/rbac/RoleBasedRender';
import { useRoleCheck } from '@/hooks/useRoleCheck';
import { useToast } from '@/hooks/use-toast';
import {
  usePersonalKeyMembers,
  useInviteDiscoveredUser,
} from '@/hooks/api/useMembers';
import {
  getImportedUserDisplay,
  type ImportedUser,
} from './imported-user-display';
import { MemberActions } from './MemberActions';
import { getHighestRole } from './getHighestRole';
import { RoleSelector } from './RoleSelector';

type Member = OrganizationMember;

/** Columns with a header filter; each row has one value per column. */
type FilterField = 'role' | 'status' | 'securityGroup';

const FILTER_OPERATORS = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
];

/**
 * Filter value for "nothing in this column". Not '': the filter popover rejects
 * an all-empty selection ("At least one value is required").
 */
// ponytail: a group literally named this would share the "No group" option.
const NO_VALUE = '__kirin_none__';

/** Shown for a row with no value in that column (NO_VALUE in the filter). */
const EMPTY_FILTER_LABEL: Partial<Record<FilterField, string>> = {
  role: 'No role',
  securityGroup: 'No group',
};

/**
 * What the Status column shows for a member row: whether they joined the org
 * (`authStatus`, from Descope), not API-key use — a support user never gets a
 * key, so key-based status left them "Invited" forever (KIR-4294). A used org
 * key still counts as joined: MDM-provisioned members never sign in, so Descope
 * can keep them `invited` while their agent runs.
 */
function getMemberStatus(member: Member): string {
  // KIR-5410: offboarded from this org (shared devices).
  if (member.offboardedAt) return 'Offboarded';
  if (member.userType === 'unattributed') return 'Unattributed';
  if (member.userType && member.userType !== 'full') return 'Discovered';
  if (
    member.hasPendingInvite ||
    (member.authStatus === OrganizationUserStatus.Invited &&
      member.deploymentStatus !== DeploymentStatus.Active)
  ) {
    return 'Invited';
  }
  if (member.authStatus === OrganizationUserStatus.Disabled) return 'Disabled';
  return 'Active';
}

/**
 * Combined display item for the table
 * Either a regular member or an imported user
 */
type DisplayItem =
  | { type: 'member'; data: Member }
  | { type: 'imported'; data: ImportedUser };

/**
 * Members table component
 */
export const MembersTable = ({
  members,
  importedUsers = [],
  currentUserId,
  organizationId,
  showStatusAndSourceColumns = false,
  showSecurityGroupColumn = false,
  securityGroupNameByUserId = {},
  securityGroupNames = [],
  searchTerm = '',
  filters = [],
  onFiltersChange,
}: {
  members: Member[];
  importedUsers?: ImportedUser[];
  currentUserId: string;
  organizationId: string;
  showStatusAndSourceColumns?: boolean;
  /** KIR-3800: gated by `security_groups_policies` at the call site. */
  showSecurityGroupColumn?: boolean;
  /** Descope user ID → security group name. Missing = user is in no group. */
  securityGroupNameByUserId?: Record<string, string>;
  /** Every group in the org, offered by the filter even with no members. */
  securityGroupNames?: string[];
  /**
   * KIR-4921: case-insensitive name / email / handle filter. Client-side over
   * the already-loaded list, so typing never reaches the backend (or Descope).
   * Must move server-side if `GET /members` is ever paginated.
   */
  searchTerm?: string;
  /**
   * Active header filters. Held by the caller so they outlive this table, which
   * unmounts while the list reloads.
   */
  filters?: FilterValue[];
  onFiltersChange?: (filters: FilterValue[]) => void;
}): ReactElement => {
  const [sortField, setSortField] = useState<
    'name' | 'email' | 'role' | 'securityGroup'
  >('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // An emptied filter (Clear, or Apply with nothing ticked) removes it.
  const handleFilterChange = (filter: FilterValue): void => {
    onFiltersChange?.([
      ...filters.filter(f => f.field !== filter.field),
      ...(filter.value.length > 0 ? [filter] : []),
    ]);
  };

  const handleSort = (field: typeof sortField): void => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: typeof sortField): string | null => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const { user: descopeUser } = useUser();
  const descopeUserId =
    (descopeUser as { userId?: string } | null)?.userId || '';

  // Owner-only view of members still on a personal API key (KIR-3786, AC2/AC3).
  // Gate the request on the owner role so non-owners never hit the 403 endpoint.
  const { hasAccess: isOwner } = useRoleCheck(
    { param: 'organizationId', anyOf: [OrganizationRole.Owner] },
    organizationId
  );
  const { data: personalKeyMembers = [] } = usePersonalKeyMembers(
    organizationId,
    isOwner
  );
  const personalKeyStateById = new Map(
    personalKeyMembers.map(m => [m.id, m.personalKeyState])
  );

  // KIR-4921: handles (a member's linked GitHub login / Bitbucket nickname, an
  // imported user's login / nickname) match with or without the leading `@`
  // the table shows them with. Discovered rows carry `name: null` and the
  // Unattributed placeholder has no email either, so the latter never matches.
  const trimmedSearch = searchTerm.trim();
  const term = trimmedSearch.toLowerCase();
  const matchesSearch = (item: DisplayItem): boolean => {
    if (!term) return true;
    let candidates: (string | null | undefined)[];
    if (item.type === 'member') {
      const { name, email, linkedGithub, linkedBitbucket } = item.data;
      candidates = [
        name,
        email,
        linkedGithub && `@${linkedGithub.login}`,
        linkedBitbucket && `@${linkedBitbucket.nickname}`,
      ];
    } else {
      const { displayName, email, fallbackHandle } = getImportedUserDisplay(
        item.data
      );
      candidates = [displayName, email, fallbackHandle && `@${fallbackHandle}`];
    }
    return candidates.some(value => !!value?.toLowerCase().includes(term));
  };

  // What a row shows in each filterable column, as the filter compares it.
  // NO_VALUE is "nothing there": imported and Discovered rows have no role or
  // group, a member with no roles shows "No Role", and most are in no group.
  const filterValueOf = (item: DisplayItem, field: FilterField): string => {
    if (item.type === 'imported') {
      return field === 'status' ? 'Imported' : NO_VALUE;
    }
    const member = item.data;
    if (field === 'status') return getMemberStatus(member);
    const isFullMember = isAccountMember(member);
    if (field === 'role') {
      return isFullMember && member.roles.length > 0
        ? getHighestRole(member.roles)
        : NO_VALUE;
    }
    // KIR-5410: people without a Descope account can be in a group too.
    return (
      (!member.offboardedAt && securityGroupNameByUserId[member.id]) || NO_VALUE
    );
  };

  const matchesFilters = (item: DisplayItem): boolean =>
    filters.every(filter => {
      const included = filter.value.includes(
        filterValueOf(item, filter.field as FilterField)
      );
      return filter.operator === FilterOperators.EXCLUDES
        ? !included
        : included;
    });

  // Combine members and imported users into display items
  // Note: Backend already filters to only return status='imported' users,
  // so no additional filter is needed here
  const allItems: DisplayItem[] = [
    ...members.map((m): DisplayItem => ({ type: 'member', data: m })),
    ...importedUsers.map((u): DisplayItem => ({ type: 'imported', data: u })),
  ];
  const displayItems = allItems.filter(
    item => matchesSearch(item) && matchesFilters(item)
  );

  // Header filter for one column. Options are the values present in the list,
  // plus every security group so an empty group can still be found, plus the
  // values already selected, so one that left the list (a renamed group, the
  // last owner) can still be seen and unticked.
  const renderFilter = (field: FilterField, label: string): ReactElement => {
    const currentFilter = filters.find(f => f.field === field);
    const values = [
      ...new Set([
        ...allItems.map(i => filterValueOf(i, field)),
        ...(field === 'securityGroup' ? securityGroupNames : []),
        ...(currentFilter?.value ?? []),
      ]),
    ];
    const options = values
      .sort((a, b) =>
        a === NO_VALUE ? 1 : b === NO_VALUE ? -1 : a.localeCompare(b)
      )
      .map(value => ({
        value,
        label:
          value === NO_VALUE
            ? (EMPTY_FILTER_LABEL[field] ?? value)
            : field === 'role'
              ? // Roles are lowercase ids; the Role column capitalizes them too.
                value.charAt(0).toUpperCase() + value.slice(1)
              : value,
      }));
    return (
      // The header cell sorts on click; the filter (and its portaled popover,
      // whose events bubble through React) must not.
      <span onClick={event => event.stopPropagation()}>
        <ColumnFilter
          className="size-5"
          fieldId={field}
          fieldName={label}
          filterOptions={{ type: FilterOptionTypes.STATIC, options }}
          availableOperators={FILTER_OPERATORS}
          columnDataType={ColumnDataTypes.ENUM}
          currentFilter={currentFilter}
          onFilterChange={handleFilterChange}
        />
      </span>
    );
  };

  // Keeps the no-results row spanning every visible column (see the header).
  const columnCount =
    4 +
    (showStatusAndSourceColumns ? 2 : 0) +
    (showSecurityGroupColumn ? 1 : 0);

  const getName = (item: DisplayItem): string => {
    if (item.type === 'member') {
      return item.data.name || item.data.email || '';
    }
    return getImportedUserDisplay(item.data).displayName;
  };

  const getEmail = (item: DisplayItem): string => {
    if (item.type === 'member') {
      return item.data.email || '';
    }
    const { email, fallbackHandle } = getImportedUserDisplay(item.data);
    return email || fallbackHandle;
  };

  const getRolePriority = (item: DisplayItem): number => {
    if (item.type === 'member') {
      const roleOrder = { owner: 0, manager: 1, member: 2 };
      return item.data.roles.reduce((min, role) => {
        const priority = roleOrder[role as keyof typeof roleOrder] ?? 3;
        return Math.min(min, priority);
      }, 3);
    }
    // Imported users have no roles, sort them after all members
    return 4;
  };

  // KIR-3800. Ungrouped users (and imported users, who can never be in a group)
  // sort last in ascending order regardless of direction-independent name
  // comparison, so the named groups always read as a contiguous block.
  const getSecurityGroupName = (item: DisplayItem): string => {
    if (item.type !== 'member') return '';
    return securityGroupNameByUserId[item.data.id] ?? '';
  };

  // Sort the display items based on current sort field and direction
  const sortedItems = [...displayItems].sort((a, b) => {
    let aValue: string | number;
    let bValue: string | number;

    switch (sortField) {
      case 'name':
        aValue = getName(a);
        bValue = getName(b);
        break;
      case 'email':
        aValue = getEmail(a);
        bValue = getEmail(b);
        break;
      case 'role':
        aValue = getRolePriority(a);
        bValue = getRolePriority(b);
        break;
      case 'securityGroup': {
        const aGroup = getSecurityGroupName(a);
        const bGroup = getSecurityGroupName(b);
        // Keep "no group" at the bottom in both directions: it is an absence,
        // not a name, so sorting it alphabetically among real groups (or to the
        // very top when descending) reads as noise.
        if (!aGroup && !bGroup) return 0;
        if (!aGroup) return 1;
        if (!bGroup) return -1;
        aValue = aGroup;
        bValue = bGroup;
        break;
      }
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div className="rounded-md border border-table-border shadow-card overflow-hidden">
      <div className="max-h-[70vh] overflow-auto">
        <table className="min-w-full text-xs">
          <thead className="sticky top-0 z-20 border-b bg-muted">
            <tr className="text-left">
              <th
                className={cn(
                  'table-head-sortable',
                  showStatusAndSourceColumns ? 'w-[22%]' : 'w-1/4'
                )}
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Name {getSortIcon('name')}
                </div>
              </th>
              <th
                className={cn(
                  'table-head-sortable',
                  showStatusAndSourceColumns ? 'w-[24%]' : 'w-1/4'
                )}
                onClick={() => handleSort('email')}
              >
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email {getSortIcon('email')}
                </div>
              </th>
              <th
                className={cn(
                  'table-head-sortable',
                  showStatusAndSourceColumns ? 'w-[14%]' : 'w-1/4'
                )}
                onClick={() => handleSort('role')}
              >
                <div className="flex items-center gap-2">
                  {renderFilter('role', 'Role')}
                  <Shield className="h-4 w-4" />
                  Role {getSortIcon('role')}
                </div>
              </th>
              {showStatusAndSourceColumns && (
                <th className="table-head-base w-[12%]">
                  <div className="flex items-center gap-2">
                    {renderFilter('status', 'Status')}
                    Status
                  </div>
                </th>
              )}
              {/*
                KIR-3800. Intentionally has no width class: the other columns'
                percentages already sum to 100, so letting this one size
                automatically re-flows them proportionally instead of forcing a
                second full set of width constants for the with/without-group
                permutations.
              */}
              {showSecurityGroupColumn && (
                <th
                  className="table-head-sortable"
                  onClick={() => handleSort('securityGroup')}
                >
                  <div className="flex items-center gap-2">
                    {renderFilter('securityGroup', 'Security Group')}
                    <ShieldUser className="h-4 w-4" />
                    Security Group {getSortIcon('securityGroup')}
                  </div>
                </th>
              )}
              {/* TODO: Add Platforms column in future phase */}
              <th
                className={cn(
                  'table-head-base',
                  showStatusAndSourceColumns ? 'w-[18%]' : 'w-1/4'
                )}
              >
                Actions
              </th>
              {showStatusAndSourceColumns && (
                <th className="table-head-base w-[10%]">Source</th>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedItems.map(item =>
              item.type === 'member' ? (
                item.data.offboardedAt ? (
                  <OffboardedUserRow
                    key={`offboarded-${item.data.id}`}
                    member={item.data}
                    showStatusAndSourceColumns={showStatusAndSourceColumns}
                    showSecurityGroupColumn={showSecurityGroupColumn}
                  />
                ) : item.data.userType && item.data.userType !== 'full' ? (
                  <DiscoveredUserRow
                    key={`discovered-${item.data.id}`}
                    member={item.data}
                    organizationId={organizationId}
                    showStatusAndSourceColumns={showStatusAndSourceColumns}
                    showSecurityGroupColumn={showSecurityGroupColumn}
                    securityGroupName={securityGroupNameByUserId[item.data.id]}
                  />
                ) : (
                  <MemberRow
                    key={`member-${item.data.id}`}
                    member={item.data}
                    currentUserId={currentUserId}
                    organizationId={organizationId}
                    descopeUserId={descopeUserId}
                    allMembers={members}
                    showStatusAndSourceColumns={showStatusAndSourceColumns}
                    showSecurityGroupColumn={showSecurityGroupColumn}
                    securityGroupName={securityGroupNameByUserId[item.data.id]}
                    personalKeyState={personalKeyStateById.get(item.data.id)}
                  />
                )
              ) : (
                <ImportedUserRow
                  key={`imported-${item.data.id}`}
                  user={item.data}
                  showStatusAndSourceColumns={showStatusAndSourceColumns}
                  showSecurityGroupColumn={showSecurityGroupColumn}
                />
              )
            )}
            {(term || filters.length > 0) && sortedItems.length === 0 && (
              <tr>
                <td
                  colSpan={columnCount}
                  className="h-12 px-3 py-6 text-center text-muted-foreground"
                >
                  {!term
                    ? 'No users match the selected filters'
                    : filters.length > 0
                      ? `No users match "${trimmedSearch}" and the selected filters`
                      : `No users match "${trimmedSearch}"`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * Row component for regular members
 */
function MemberRow({
  member,
  currentUserId,
  organizationId,
  descopeUserId,
  allMembers,
  showStatusAndSourceColumns,
  showSecurityGroupColumn,
  securityGroupName,
  personalKeyState,
}: {
  member: Member;
  currentUserId: string;
  organizationId: string;
  descopeUserId: string;
  allMembers: Member[];
  showStatusAndSourceColumns: boolean;
  showSecurityGroupColumn: boolean;
  securityGroupName?: string;
  personalKeyState?: PersonalKeyState;
}): ReactElement {
  const status = getMemberStatus(member);
  const isActive = status === 'Active';
  const isDisabled = status === 'Disabled';
  const isInvited = status === 'Invited';
  const hasLinkedGithub = !!member.linkedGithub;
  const hasLinkedBitbucket = !!member.linkedBitbucket;
  const hasAnyLinkedProvider = hasLinkedGithub || hasLinkedBitbucket;
  const displayName = member.name || member.email || '';

  const avatarSource = member.linkedGithub?.avatarUrl
    ? member.linkedGithub
    : member.linkedBitbucket?.avatarUrl
      ? member.linkedBitbucket
      : (member.linkedGithub ?? member.linkedBitbucket);
  const linkedAvatarUrl = avatarSource?.avatarUrl;
  const linkedAvatarAlt = avatarSource
    ? 'login' in avatarSource
      ? avatarSource.login
      : avatarSource.nickname
    : displayName;

  /**
   * Checks if target member is not the current user (self-removal check).
   * Role-based permission checks (owner/manager) are handled by RoleBasedRender.
   */
  const isNotSelf = (targetMember: Member): boolean => {
    return descopeUserId !== '' && targetMember.id !== descopeUserId;
  };

  /**
   * Checks if target member is an owner (case-insensitive).
   *
   * A pending invite is NOT an owner yet: its `roles` carries the role the
   * invite will grant on accept (KIR-4040), not a live membership. Gating a
   * manager's Delete/Resend on it would block managers from managing invitations
   * they are allowed to create, even though removal keys off Descope membership,
   * not the invite role — so pending invites are treated as non-owner here.
   */
  const isTargetOwner = (targetMember: Member): boolean => {
    if (targetMember.hasPendingInvite === true) {
      return false;
    }
    return targetMember.roles
      .map(r => r.toLowerCase())
      .includes(OrganizationRole.Owner.toLowerCase());
  };

  return (
    <tr
      className={cn('border-b hover:bg-muted/50 transition-colors', {
        'opacity-60': isInvited,
      })}
    >
      <td className="min-w-0 h-12 px-3 py-2 font-medium">
        <div className="flex min-w-0 items-center gap-2">
          {linkedAvatarUrl ? (
            <img
              src={linkedAvatarUrl}
              alt={linkedAvatarAlt}
              className="h-6 w-6 shrink-0 rounded-full"
            />
          ) : hasAnyLinkedProvider ? (
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-medium dark:bg-slate-700">
              {linkedAvatarAlt.slice(0, 2).toUpperCase()}
            </div>
          ) : null}
          <span className="min-w-0">{displayName}</span>
          {personalKeyState && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  variant="outline"
                  className="shrink-0 cursor-default border-amber-500/50 text-amber-600 dark:text-amber-400"
                >
                  {personalKeyState === PersonalKeyState.Active
                    ? 'Active personal key'
                    : 'Generated personal key'}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                {personalKeyState === PersonalKeyState.Active
                  ? 'This member used a personal API key in the last 7 days.'
                  : 'This member generated a personal API key in the last 7 days but has not used it yet.'}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </td>
      <td className="min-w-0 h-12 px-3 py-2 text-muted-foreground">
        {member.email}
      </td>
      <td className="min-w-0 h-12 px-3 py-2">
        <RoleSelector
          member={member}
          currentUserId={currentUserId}
          organizationId={organizationId}
          allMembers={allMembers}
        />
      </td>
      {showStatusAndSourceColumns && (
        <td className="min-w-0 h-12 px-3 py-2">
          {isActive ? (
            <Badge>Active</Badge>
          ) : isDisabled ? (
            <Badge variant="outline" className="text-muted-foreground">
              Disabled
            </Badge>
          ) : (
            <Badge variant="outline">Invited</Badge>
          )}
        </td>
      )}
      {showSecurityGroupColumn && (
        <td className="min-w-0 h-12 px-3 py-2">
          {securityGroupName ? (
            <Badge variant="outline">{securityGroupName}</Badge>
          ) : (
            // AC #11: a user in no group shows a dash (they use org policies).
            <span className="text-muted-foreground">—</span>
          )}
        </td>
      )}
      {/* TODO: Add Platforms column in future phase */}
      <td className="min-w-0 h-12 px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <RoleBasedRender
            roles={{
              param: 'organizationId',
              anyOf: [OrganizationRole.Owner],
            }}
            organizationId={organizationId}
            showFallback={true}
            fallback={
              <RoleBasedRender
                roles={{
                  param: 'organizationId',
                  anyOf: [OrganizationRole.Manager],
                }}
                organizationId={organizationId}
                showFallback={true}
                fallback={
                  <MemberActions
                    member={member}
                    canRemove={false}
                    organizationId={organizationId}
                  />
                }
              >
                {/* Manager: can remove unless target is owner or self */}
                <MemberActions
                  member={member}
                  canRemove={isNotSelf(member) && !isTargetOwner(member)}
                  organizationId={organizationId}
                />
              </RoleBasedRender>
            }
          >
            {/* Owner: can remove anyone except self */}
            <MemberActions
              member={member}
              canRemove={isNotSelf(member)}
              organizationId={organizationId}
            />
          </RoleBasedRender>
        </div>
      </td>
      {showStatusAndSourceColumns && (
        <td className="min-w-0 h-12 px-3 py-2">
          {hasAnyLinkedProvider ? (
            <span className="inline-flex items-center gap-2 text-muted-foreground">
              {hasLinkedGithub && (
                <span className="inline-flex items-center gap-1">
                  <Github className="h-3 w-3" />
                  <span className="text-xs">GitHub</span>
                </span>
              )}
              {hasLinkedBitbucket && (
                <span className="inline-flex items-center gap-1">
                  <BitbucketIcon className="h-3 w-3" />
                  <span className="text-xs">Bitbucket</span>
                </span>
              )}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
      )}
    </tr>
  );
}

/**
 * Row component for imported (unlinked) users from any source control provider
 */
function ImportedUserRow({
  user,
  showStatusAndSourceColumns,
  showSecurityGroupColumn,
}: {
  user: ImportedUser;
  showStatusAndSourceColumns: boolean;
  showSecurityGroupColumn: boolean;
}): ReactElement {
  const { displayName, avatarUrl, email, fallbackHandle, provider } =
    getImportedUserDisplay(user);

  return (
    <tr className="border-b hover:bg-muted/50 transition-colors opacity-60">
      <td className="min-w-0 h-12 px-3 py-2 font-medium">
        <div className="flex min-w-0 items-center gap-2">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={displayName}
              className="h-6 w-6 shrink-0 rounded-full"
            />
          ) : (
            <div className="h-6 w-6 shrink-0 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-medium">
              {displayName.slice(0, 2).toUpperCase()}
            </div>
          )}
          <span className="min-w-0">{displayName}</span>
        </div>
      </td>
      <td className="min-w-0 h-12 px-3 py-2 text-muted-foreground">
        {email || (
          <span className="text-muted-foreground/50 italic">
            @{fallbackHandle}
          </span>
        )}
      </td>
      <td className="min-w-0 h-12 px-3 py-2">
        <span className="text-muted-foreground">—</span>
      </td>
      {showStatusAndSourceColumns && (
        <td className="min-w-0 h-12 px-3 py-2">
          <Badge variant="secondary">Imported</Badge>
        </td>
      )}
      {showSecurityGroupColumn && (
        // An imported user is not an organization member yet, so it can never
        // belong to a security group — but the cell must exist to keep the
        // column count aligned with the header.
        <td className="min-w-0 h-12 px-3 py-2">
          <span className="text-muted-foreground">—</span>
        </td>
      )}
      <td className="min-w-0 h-12 px-3 py-2">
        <span className="text-muted-foreground/50 italic text-xs">
          Link account to enable
        </span>
      </td>
      {showStatusAndSourceColumns && (
        <td className="min-w-0 h-12 px-3 py-2">
          <ProviderLabel provider={provider} />
        </td>
      )}
    </tr>
  );
}

/**
 * KIR-3908: row for a Discovered user (a Cowork user with no Kirin account) or
 * the per-org Unattributed placeholder. No seat, no role; a Discovered user can
 * be invited (converting to a full account, keeping history), the Unattributed
 * placeholder cannot.
 */
function DiscoveredUserRow({
  member,
  organizationId,
  showStatusAndSourceColumns,
  showSecurityGroupColumn,
  securityGroupName,
}: {
  member: Member;
  organizationId: string;
  showStatusAndSourceColumns: boolean;
  showSecurityGroupColumn: boolean;
  /** KIR-5410: the person's security group (shared devices). */
  securityGroupName?: string;
}): ReactElement {
  const isUnattributed = member.userType === 'unattributed';
  // KIR-5410: a person seen on a shared endpoint shows their OS account name.
  const seenOnEndpoint = member.source === 'endpoint';
  const label =
    member.name || (isUnattributed ? 'Unattributed' : 'Unknown name');
  const hint = seenOnEndpoint
    ? 'OS account, no Kirin account'
    : isUnattributed
      ? 'Email missing or not valid'
      : 'No Kirin account';
  const { mutateAsync: invite, isPending } = useInviteDiscoveredUser();
  const { toast } = useToast();

  const handleInvite = async (): Promise<void> => {
    // Surface both outcomes (mirrors AddMemberModal): the invite can reject with
    // NOT_A_DISCOVERED_USER / DISCOVERED_USER_NOT_FOUND / 409 / a Descope error,
    // and there is no MutationCache onError, so without this the failure would be
    // a silent unhandled rejection and the button would just re-enable.
    try {
      await invite({ organizationId, userId: member.id });
      toast({
        title: 'Invitation sent',
        description: member.email
          ? `Invited ${member.email}`
          : 'Invitation sent',
        variant: 'success',
      });
    } catch (error) {
      toast({
        title: 'Could not send invitation',
        description:
          error instanceof Error ? error.message : 'Failed to invite user',
        variant: 'destructive',
      });
      console.error('Failed to invite discovered user:', error);
    }
  };

  return (
    <tr className="border-b hover:bg-muted/50 transition-colors opacity-80">
      <td className="min-w-0 h-12 px-3 py-2 font-medium">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-medium text-muted-foreground dark:bg-slate-700">
            {isUnattributed ? '–' : '?'}
          </div>
          {/* Stack the label over its hint: side-by-side spans overlapped once
              the column narrowed (a shrunk `min-w-0` label overflows onto a
              `shrink-0` hint). A truncating column can't collide. */}
          <div className="flex min-w-0 flex-col">
            <span
              className={cn(
                'truncate',
                member.name ? '' : 'italic text-muted-foreground'
              )}
            >
              {label}
            </span>
            <span className="truncate text-xs text-muted-foreground/70">
              {hint}
            </span>
          </div>
        </div>
      </td>
      <td className="min-w-0 h-12 px-3 py-2 text-muted-foreground">
        {member.email || (
          <span className="text-muted-foreground/50 italic">
            no email captured
          </span>
        )}
      </td>
      <td className="min-w-0 h-12 px-3 py-2">
        <span className="text-muted-foreground/50 italic">No seat</span>
      </td>
      {showStatusAndSourceColumns && (
        <td className="min-w-0 h-12 px-3 py-2">
          {isUnattributed ? (
            <Badge variant="outline" className="text-muted-foreground">
              Unattributed
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-300"
            >
              Discovered
            </Badge>
          )}
        </td>
      )}
      {showSecurityGroupColumn && (
        // KIR-5410: with shared devices a Discovered/Unattributed person can be
        // in a group; otherwise the cell keeps the column count aligned.
        <td className="min-w-0 h-12 px-3 py-2">
          {securityGroupName ? (
            <span className="truncate">{securityGroupName}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
      )}
      <td className="min-w-0 h-12 px-3 py-2">
        {isUnattributed ? (
          <Button variant="outline" size="sm" disabled>
            Invite
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={handleInvite}
            disabled={isPending}
          >
            {isPending ? 'Inviting…' : 'Invite'}
          </Button>
        )}
      </td>
      {showStatusAndSourceColumns && (
        <td className="min-w-0 h-12 px-3 py-2">
          <span className="text-muted-foreground">
            {seenOnEndpoint ? 'Endpoint' : 'Cowork'}
          </span>
        </td>
      )}
    </tr>
  );
}

/**
 * KIR-5410: a person offboarded from this org (removed or IdP-offboarded, not
 * re-added). Kept on the list, labelled Offboarded, so their history stays
 * findable; no seat, no role, no group and no actions (re-invite with Add Member).
 */
function OffboardedUserRow({
  member,
  showStatusAndSourceColumns,
  showSecurityGroupColumn,
}: {
  member: Member;
  showStatusAndSourceColumns: boolean;
  showSecurityGroupColumn: boolean;
}): ReactElement {
  return (
    <tr className="border-b hover:bg-muted/50 transition-colors opacity-80">
      <td className="min-w-0 h-12 px-3 py-2 font-medium">
        {/* The Status column (always shown while offboarded rows exist,
            see MembersTab) carries the Offboarded label. */}
        <span className="block truncate">
          {member.name || member.email || 'Unknown name'}
        </span>
      </td>
      <td className="min-w-0 h-12 px-3 py-2 text-muted-foreground">
        {member.email || (
          <span className="text-muted-foreground/50 italic">
            no email captured
          </span>
        )}
      </td>
      <td className="min-w-0 h-12 px-3 py-2">
        <span className="text-muted-foreground/50 italic">No seat</span>
      </td>
      {showStatusAndSourceColumns && (
        <td className="min-w-0 h-12 px-3 py-2">
          <Badge
            variant="outline"
            className="border-red-300 text-red-700 dark:border-red-800 dark:text-red-300"
          >
            Offboarded
          </Badge>
        </td>
      )}
      {showSecurityGroupColumn && (
        <td className="min-w-0 h-12 px-3 py-2">
          <span className="text-muted-foreground">—</span>
        </td>
      )}
      <td className="min-w-0 h-12 px-3 py-2">
        <span className="text-muted-foreground">—</span>
      </td>
      {showStatusAndSourceColumns && (
        <td className="min-w-0 h-12 px-3 py-2">
          <span className="text-muted-foreground">—</span>
        </td>
      )}
    </tr>
  );
}

function ProviderLabel({
  provider,
}: {
  provider: ImportedUser['provider'];
}): ReactElement {
  if (provider === 'github') {
    return (
      <span className="inline-flex items-center gap-1 text-muted-foreground">
        <Github className="h-3 w-3" />
        GitHub
      </span>
    );
  } else if (provider === 'bitbucket') {
    return (
      <span className="inline-flex items-center gap-1 text-muted-foreground">
        <BitbucketIcon className="h-3 w-3" />
        Bitbucket
      </span>
    );
  }
  const _exhaustive: never = provider;
  throw new Error(`Unhandled provider: ${String(_exhaustive)}`);
}
